/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { AppContext } from "utils/AppContext";
import { LoadingOverlay } from "components/LoadingOverlay";
import {
  IRunnersStatusResponse,
  IWorkflowMetricDataPoint,
  IWorkflowRunSnapshot,
  IWorkflowSummaryEntry,
  useWorkflowsMonitoringService,
} from "services/registry/WorkflowsMonitoringService";
import { IntegerInputPopup } from "components/IntegerInputPopup";

const TIME_RANGE_OPTIONS = [
  { label: "Last 1 hour", value: 1 },
  { label: "Last 6 hours", value: 6 },
  { label: "Last 24 hours", value: 24 },
  { label: "Last 7 days", value: 168 },
];

const formatDuration = (ms: number): string => {
  if (ms <= 0) return "N/A";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
};

const formatTimestamp = (ts: number): string => {
  const date = new Date(ts);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const getConclusionColor = (conclusion: string | null): "success" | "error" | "warning" | "info" | "default" => {
  switch (conclusion) {
    case "success":
      return "success";
    case "failure":
      return "error";
    case "none":
    case null:
      return "warning";
    default:
      return "default";
  }
};

const getStatusColor = (status: string): "success" | "error" | "warning" | "info" | "default" => {
  switch (status) {
    case "completed":
      return "success";
    case "in_progress":
      return "info";
    case "queued":
      return "warning";
    default:
      return "default";
  }
};

const safeRuns = (runs: IWorkflowRunSnapshot[] | undefined): IWorkflowRunSnapshot[] => runs ?? [];

const getPrimaryRun = (dp: IWorkflowMetricDataPoint): IWorkflowRunSnapshot | null => {
  const current = safeRuns(dp.currentRuns);
  if (current.length > 0) {
    return current.reduce((a, b) => (a.updatedAt > b.updatedAt ? a : b));
  }
  const completed = safeRuns(dp.recentlyCompletedRuns);
  if (completed.length > 0) {
    return completed.reduce((a, b) => (a.updatedAt > b.updatedAt ? a : b));
  }
  return null;
};

const getMaxCompletedDurationMs = (dp: IWorkflowMetricDataPoint): number => {
  const completed = safeRuns(dp.recentlyCompletedRuns);
  if (completed.length === 0) return 0;
  return Math.max(...completed.map((r) => r.durationMs));
};

interface IFailureRow {
  id: number;
  workflowName: string;
  timestamp: number;
  runId: number;
  status: string;
  conclusion: string;
  headBranch: string;
  durationMs: number;
}

const BUCKET_SIZE_MS: Record<number, number> = {
  1: 5 * 60 * 1000, // 5 minutes
  6: 20 * 60 * 1000, // 20 minutes
  24: 60 * 60 * 1000, // 1 hour
  168: 6 * 60 * 60 * 1000, // 6 hours
};

const bucketDataPoints = (
  dataPoints: IWorkflowMetricDataPoint[],
  timeRangeHours: number,
): { label: string; successes: number; failures: number }[] => {
  const bucketSizeMs = BUCKET_SIZE_MS[timeRangeHours] ?? 60 * 60 * 1000;
  const now = Date.now();
  const rangeStart = now - timeRangeHours * 60 * 60 * 1000;
  const totalBuckets = Math.ceil((now - rangeStart) / bucketSizeMs);
  const includeDate = timeRangeHours > 24;

  const pointsByBucket = new Map<number, IWorkflowMetricDataPoint[]>();
  for (const dp of dataPoints) {
    const key = Math.floor((dp.timestamp - rangeStart) / bucketSizeMs);
    if (key < 0 || key >= totalBuckets) continue;
    if (!pointsByBucket.has(key)) pointsByBucket.set(key, []);
    pointsByBucket.get(key)!.push(dp);
  }

  return Array.from({ length: totalBuckets }, (_, i) => {
    const bucketCenter = rangeStart + i * bucketSizeMs + bucketSizeMs / 2;
    const date = new Date(bucketCenter);
    const label = includeDate
      ? `${date.toLocaleDateString([], { month: "short", day: "numeric" })} ${formatTimestamp(bucketCenter)}`
      : formatTimestamp(bucketCenter);
    const points = pointsByBucket.get(i);
    return {
      label,
      successes: points ? Math.round(points.reduce((s, p) => s + p.successCountLastHour, 0) / points.length) : 0,
      failures: points ? Math.round(points.reduce((s, p) => s + p.failedCountLastHour, 0) / points.length) : 0,
    };
  });
};

const bucketTimeSeries = (
  dataPoints: IWorkflowMetricDataPoint[],
  timeRangeHours: number,
  extractor: (dp: IWorkflowMetricDataPoint) => number,
): { timestamps: Date[]; values: (number | null)[] } => {
  const bucketSizeMs = BUCKET_SIZE_MS[timeRangeHours] ?? 60 * 60 * 1000;
  const now = Date.now();
  const rangeStart = now - timeRangeHours * 60 * 60 * 1000;
  const totalBuckets = Math.ceil((now - rangeStart) / bucketSizeMs);

  const pointsByBucket = new Map<number, IWorkflowMetricDataPoint[]>();
  for (const dp of dataPoints) {
    const key = Math.floor((dp.timestamp - rangeStart) / bucketSizeMs);
    if (key < 0 || key >= totalBuckets) continue;
    if (!pointsByBucket.has(key)) pointsByBucket.set(key, []);
    pointsByBucket.get(key)!.push(dp);
  }

  const timestamps: Date[] = [];
  const values: (number | null)[] = [];
  for (let i = 0; i < totalBuckets; i++) {
    timestamps.push(new Date(rangeStart + i * bucketSizeMs + bucketSizeMs / 2));
    const points = pointsByBucket.get(i);
    values.push(points ? points.reduce((s, p) => s + extractor(p), 0) / points.length : null);
  }
  return { timestamps, values };
};

const SELECTED_WORKFLOW_STORAGE_KEY = "workflowsMonitoring.selectedWorkflow";

const getWorkflowGitHubUrl = (workflowName: string): string => {
  const slashIndex = workflowName.indexOf("/");
  const repo = workflowName.substring(0, slashIndex);
  const workflowFile = workflowName.substring(slashIndex + 1);
  return `https://github.com/FortisGames/${repo}/actions/workflows/${workflowFile}`;
};

const getRunGitHubUrl = (workflowName: string, runId: number): string => {
  const repo = workflowName.substring(0, workflowName.indexOf("/"));
  return `https://github.com/FortisGames/${repo}/actions/runs/${runId}`;
};

const WorkflowsMonitoring = () => {
  const context = useContext(AppContext);
  const service = useWorkflowsMonitoringService();

  const [loading, setLoading] = useState(true);
  const [summaryEntries, setSummaryEntries] = useState<IWorkflowSummaryEntry[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>(
    () => localStorage.getItem(SELECTED_WORKFLOW_STORAGE_KEY) || "",
  );
  const selectedWorkflowRef = useRef(selectedWorkflow);
  const [metricsDataPoints, setMetricsDataPoints] = useState<IWorkflowMetricDataPoint[]>([]);
  const [failureRows, setFailureRows] = useState<IFailureRow[]>([]);
  const [timeRangeHours, setTimeRangeHours] = useState(24);
  const [activeTab, setActiveTab] = useState(0);
  const [runnersData, setRunnersData] = useState<IRunnersStatusResponse | null>(null);
  const [runnersLoading, setRunnersLoading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; severity: AlertColor } | null>(null);
  const [forceCancelPopupVisible, setForceCancelPopupVisible] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    selectedWorkflowRef.current = selectedWorkflow;
    if (selectedWorkflow) {
      localStorage.setItem(SELECTED_WORKFLOW_STORAGE_KEY, selectedWorkflow);
    }
  }, [selectedWorkflow]);

  useEffect(() => {
    if (context) {
      context.setTitle("Workflows Monitoring");
      context.setTargetAPI("registry");
    }
    loadSummary();

    intervalRef.current = setInterval(loadSummary, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedWorkflow) {
      loadMetrics();
      loadFailures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkflow, timeRangeHours]);

  const loadSummary = async () => {
    try {
      const response = await service.getWorkflowSummary();
      setSummaryEntries(response.workflows);
      if (!selectedWorkflowRef.current && response.workflows.length > 0) {
        setSelectedWorkflow(response.workflows[0].workflowName);
      }
    } catch (error: any) {
      setNotification({ message: error.debugMessage || "Failed to load workflow summary", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const from = Date.now() - timeRangeHours * 60 * 60 * 1000;
      const response = await service.getWorkflowMetrics(selectedWorkflow, from);
      setMetricsDataPoints(response.dataPoints);
    } catch (error: any) {
      setNotification({ message: error.debugMessage || "Failed to load metrics", severity: "error" });
    }
  };

  const loadFailures = async () => {
    try {
      const from = Date.now() - timeRangeHours * 60 * 60 * 1000;
      const response = await service.getWorkflowFailures(from);
      const seen = new Set<number>();
      const rows: IFailureRow[] = [];
      for (const dp of response.dataPoints) {
        for (const run of safeRuns(dp.recentlyCompletedRuns)) {
          if (run.conclusion !== "success" && run.conclusion !== null && !seen.has(run.runId)) {
            seen.add(run.runId);
            rows.push({
              id: run.runId,
              workflowName: dp.workflowName,
              timestamp: dp.timestamp,
              runId: run.runId,
              status: run.status,
              conclusion: run.conclusion,
              headBranch: run.headBranch,
              durationMs: run.durationMs,
            });
          }
        }
      }
      rows.sort((a, b) => b.timestamp - a.timestamp);
      setFailureRows(rows);
    } catch (error: any) {
      setNotification({ message: error.debugMessage || "Failed to load failures", severity: "error" });
    }
  };

  const loadRunners = async () => {
    setRunnersLoading(true);
    try {
      const response = await service.getRunnersStatus();
      setRunnersData(response);
    } catch (error: any) {
      setNotification({ message: error.debugMessage || "Failed to load runners status", severity: "error" });
    } finally {
      setRunnersLoading(false);
    }
  };

  const handleForceCancelWorkflowRun = async (runId: number) => {
    const repo = selectedWorkflow.substring(0, selectedWorkflow.indexOf("/"));
    try {
      await service.forceCancelWorkflowRun(repo, runId);
      setNotification({ message: `Force-cancelled workflow run ${runId}`, severity: "success" });
    } catch (error: any) {
      setNotification({ message: error.debugMessage || "Failed to force-cancel workflow run", severity: "error" });
    }
    setForceCancelPopupVisible(false);
  };

  useEffect(() => {
    if (activeTab === 2 && !runnersData && !runnersLoading) {
      loadRunners();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const selectedEntry = summaryEntries.find((e) => e.workflowName === selectedWorkflow);

  const renderSummaryCards = () => (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
      {summaryEntries.map((entry) => {
        const dp = entry.latestDataPoint;
        const isSelected = entry.workflowName === selectedWorkflow;
        return (
          <Card
            key={entry.workflowName}
            sx={{
              minWidth: 260,
              maxWidth: 320,
              flex: "1 1 260px",
              border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
            }}
          >
            <CardActionArea onClick={() => setSelectedWorkflow(entry.workflowName)}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {entry.displayName}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {entry.workflowName}
                  </Typography>
                  <IconButton
                    size="small"
                    href={getWorkflowGitHubUrl(entry.workflowName)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0 }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                {dp ? (
                  <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {(() => {
                      const run = getPrimaryRun(dp);
                      if (!run) return null;
                      return (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <Chip label={run.status} size="small" color={getStatusColor(run.status)} />
                          {run.conclusion && (
                            <Chip
                              label={run.conclusion}
                              size="small"
                              color={getConclusionColor(run.conclusion)}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      );
                    })()}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                      <Typography variant="body2">
                        Success rate: <b>{dp.successRate}%</b>
                      </Typography>
                      <Typography variant="body2">
                        Queued: <b>{dp.queuedCount}</b>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">
                        Failures (1h): <b>{dp.failedCountLastHour}</b>
                      </Typography>
                      <Typography variant="body2">
                        Duration: <b>{formatDuration(getMaxCompletedDurationMs(dp))}</b>
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No data available
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );

  const renderToolbar = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
      {selectedEntry && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="h6">{selectedEntry.displayName}</Typography>
          <IconButton
            size="small"
            href={getWorkflowGitHubUrl(selectedEntry.workflowName)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Time Range</InputLabel>
        <Select value={timeRangeHours} label="Time Range" onChange={(e) => setTimeRangeHours(e.target.value as number)}>
          {TIME_RANGE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const renderQueueChart = () => {
    if (metricsDataPoints.length === 0) return <NoDataMessage />;
    const queued = bucketTimeSeries(metricsDataPoints, timeRangeHours, (d) => d.queuedCount);
    const inProgress = bucketTimeSeries(metricsDataPoints, timeRangeHours, (d) => d.inProgressCount);
    return (
      <LineChart
        xAxis={[
          {
            data: queued.timestamps,
            label: "Time",
            scaleType: "time",
            valueFormatter: (v: Date) => formatTimestamp(v.getTime()),
          },
        ]}
        series={[
          {
            data: queued.values,
            label: "Queued",
            color: "#ff9800",
            curve: "linear",
            connectNulls: true,
          },
          {
            data: inProgress.values,
            label: "In Progress",
            color: "#2196f3",
            curve: "linear",
            connectNulls: true,
          },
        ]}
        height={300}
        skipAnimation
      />
    );
  };

  const renderSuccessFailureChart = () => {
    if (metricsDataPoints.length === 0) return <NoDataMessage />;
    const bucketed = bucketDataPoints(metricsDataPoints, timeRangeHours);
    return (
      <BarChart
        xAxis={[
          {
            data: bucketed.map((d) => d.label),
            label: "Time",
            scaleType: "band",
          },
        ]}
        series={[
          {
            data: bucketed.map((d) => d.successes),
            label: "Successes (1h)",
            color: "#4caf50",
          },
          {
            data: bucketed.map((d) => d.failures),
            label: "Failures (1h)",
            color: "#f44336",
          },
        ]}
        height={300}
        skipAnimation
      />
    );
  };

  const renderSuccessRateChart = () => {
    if (metricsDataPoints.length === 0) return <NoDataMessage />;
    const rate = bucketTimeSeries(metricsDataPoints, timeRangeHours, (d) => d.successRate);
    return (
      <LineChart
        xAxis={[
          {
            data: rate.timestamps,
            label: "Time",
            scaleType: "time",
            valueFormatter: (v: Date) => formatTimestamp(v.getTime()),
          },
        ]}
        yAxis={[{ min: 0, max: 100, label: "%" }]}
        series={[
          {
            data: rate.values,
            label: "Success Rate (%)",
            color: "#4caf50",
            curve: "linear",
            connectNulls: true,
          },
        ]}
        height={300}
        skipAnimation
      />
    );
  };

  const renderDurationChart = () => {
    if (metricsDataPoints.length === 0) return <NoDataMessage />;
    const avg = bucketTimeSeries(metricsDataPoints, timeRangeHours, (d) => d.avgDurationMs / 60000);
    const latest = bucketTimeSeries(metricsDataPoints, timeRangeHours, (d) => getMaxCompletedDurationMs(d) / 60000);
    return (
      <LineChart
        xAxis={[
          {
            data: avg.timestamps,
            label: "Time",
            scaleType: "time",
            valueFormatter: (v: Date) => formatTimestamp(v.getTime()),
          },
        ]}
        yAxis={[{ label: "Duration (min)" }]}
        series={[
          {
            data: avg.values,
            label: "Avg Duration",
            color: "#9c27b0",
            curve: "linear",
            connectNulls: true,
          },
          {
            data: latest.values,
            label: "Max Completed Duration",
            color: "#ff7043",
            curve: "linear",
            connectNulls: true,
          },
        ]}
        height={300}
        skipAnimation
      />
    );
  };

  const failuresColumns: GridColDef[] = [
    {
      field: "workflowName",
      headerName: "Workflow",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
          <IconButton
            size="small"
            href={getRunGitHubUrl(params.row.workflowName, params.row.runId)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ p: 0 }}
          >
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "headBranch",
      headerName: "Branch",
      width: 180,
    },
    {
      field: "timestamp",
      headerName: "Time",
      width: 200,
      valueFormatter: (value: number) => new Date(value).toLocaleString(),
    },
    {
      field: "conclusion",
      headerName: "Conclusion",
      width: 130,
      renderCell: (params) => <Chip label={params.value} size="small" color={getConclusionColor(params.value)} />,
    },
    {
      field: "durationMs",
      headerName: "Duration",
      width: 130,
      valueFormatter: (value: number) => formatDuration(value),
    },
  ];

  const renderChartsTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {renderToolbar()}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Queue & In-Progress
            </Typography>
            {renderQueueChart()}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Successes & Failures (Last Hour)
            </Typography>
            {renderSuccessFailureChart()}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Success Rate
            </Typography>
            {renderSuccessRateChart()}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Run Duration
            </Typography>
            {renderDurationChart()}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  const renderFailuresTab = () => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h6">Recent Failures</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Time Range</InputLabel>
          <Select value={timeRangeHours} label="Time Range" onChange={(e) => setTimeRangeHours(e.target.value as number)}>
            {TIME_RANGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <DataGrid
        rows={failureRows}
        columns={failuresColumns}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
      />
    </Box>
  );

  const getRunnerStatusColor = (status: string): "success" | "error" | "default" => {
    switch (status) {
      case "online":
        return "success";
      case "offline":
        return "error";
      default:
        return "default";
    }
  };

  const repoRunnerColumns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => <Chip label={params.value} size="small" color={getRunnerStatusColor(params.value)} />,
    },
    {
      field: "busy",
      headerName: "Busy",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Busy" : "Idle"}
          size="small"
          color={params.value ? "warning" : "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "labels",
      headerName: "Labels",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {(params.value as string[]).map((label) => (
            <Chip key={label} label={label} size="small" variant="outlined" />
          ))}
        </Box>
      ),
    },
  ];

  const fcb2RunnerColumns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "groupId", headerName: "Pool", width: 200 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => <Chip label={params.value} size="small" color={getRunnerStatusColor(params.value)} />,
    },
    {
      field: "busy",
      headerName: "Busy",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Busy" : "Idle"}
          size="small"
          color={params.value ? "warning" : "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "labels",
      headerName: "Labels",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {(params.value as string[]).map((label) => (
            <Chip key={label} label={label} size="small" variant="outlined" />
          ))}
        </Box>
      ),
    },
  ];

  const renderRunnersSummary = (
    label: string,
    stats: { total: number; online: number; offline: number; busy: number; idle: number },
  ) => (
    <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
      <Typography variant="body2">
        Total: <b>{stats.total}</b>
      </Typography>
      <Typography variant="body2" color="success.main">
        Online: <b>{stats.online}</b>
      </Typography>
      <Typography variant="body2" color="error.main">
        Offline: <b>{stats.offline}</b>
      </Typography>
      <Typography variant="body2" color="warning.main">
        Busy: <b>{stats.busy}</b>
      </Typography>
      <Typography variant="body2">
        Idle: <b>{stats.idle}</b>
      </Typography>
    </Box>
  );

  const renderRunnersTab = () => {
    if (runnersLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading runners status... (This may take up to 30 seconds)
          </Typography>
        </Box>
      );
    }

    const repoRunners = runnersData
      ? runnersData.repos.flatMap((repo) => repo.runners.map((r) => ({ ...r, repo: repo.repo })))
      : [];

    const fcb2Runners = runnersData
      ? runnersData.fcb2Pools.flatMap((pool) => pool.runners.map((r) => ({ ...r, groupId: pool.groupId })))
      : [];

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6">Runners Status</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadRunners}
            disabled={runnersLoading}
          >
            Refresh
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {runnersData && (
            <Typography variant="body2" color="text.secondary">
              Last fetched: {new Date(runnersData.fetchedAt).toLocaleString()}
            </Typography>
          )}
        </Box>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Self-Hosted Runners
            </Typography>
            {runnersData && runnersData.repos.length > 0 ? (
              <>
                {runnersData.repos.map((repo) => (
                  <Box key={repo.repo} sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {repo.repo}
                    </Typography>
                    {renderRunnersSummary(repo.repo, repo)}
                  </Box>
                ))}
                <DataGrid
                  rows={repoRunners}
                  columns={repoRunnerColumns}
                  autoHeight
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  disableRowSelectionOnClick
                  getRowId={(row) => `${row.repo}-${row.id}`}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No self-hosted runners found.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              FCB2 Pool Runners
            </Typography>
            {runnersData && runnersData.fcb2Pools.length > 0 ? (
              <>
                {runnersData.fcb2Pools.map((pool) => (
                  <Box key={pool.groupId} sx={{ mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {pool.groupId}
                      </Typography>
                      <Chip
                        label={pool.conclusion}
                        size="small"
                        color={getConclusionColor(pool.conclusion)}
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        ({formatDuration(pool.durationMs)})
                      </Typography>
                    </Box>
                    {pool.error && (
                      <Alert severity="error" sx={{ my: 1 }}>
                        {pool.error}
                      </Alert>
                    )}
                    {renderRunnersSummary(pool.groupId, {
                      total: pool.totalRunners,
                      online: pool.online,
                      offline: pool.offline,
                      busy: pool.busy,
                      idle: pool.idle,
                    })}
                  </Box>
                ))}
                <DataGrid
                  rows={fcb2Runners}
                  columns={fcb2RunnerColumns}
                  autoHeight
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  disableRowSelectionOnClick
                  getRowId={(row) => `${row.groupId}-${row.id}`}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No FCB2 pools configured.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (loading) return <LoadingOverlay />;

  if (summaryEntries.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="text.secondary">
          No monitored workflows configured. Enable monitoring via the GrowthBook feature flag.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          color="error"
          disabled={!selectedWorkflow}
          onClick={() => setForceCancelPopupVisible(true)}
        >
          Force Cancel Workflow Run
        </Button>
        <Button
          variant="outlined"
          size="small"
          endIcon={<OpenInNewIcon />}
          href="https://app.growthbook.io/features/workflows-monitoring-config"
          target="_blank"
          rel="noopener noreferrer"
        >
          Configure
        </Button>
      </Box>
      {renderSummaryCards()}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Charts" />
          <Tab label="Failures" />
          <Tab label="Runners" />
        </Tabs>
      </Box>

      {activeTab === 0 && renderChartsTab()}
      {activeTab === 1 && renderFailuresTab()}
      {activeTab === 2 && renderRunnersTab()}

      {forceCancelPopupVisible && (
        <IntegerInputPopup
          visible={forceCancelPopupVisible}
          title="Force Cancel Workflow Run"
          fieldName="Run ID"
          minValue={1}
          onDismiss={() => setForceCancelPopupVisible(false)}
          onAccept={handleForceCancelWorkflowRun}
        />
      )}

      {notification && (
        <Snackbar open autoHideDuration={6000} onClose={() => setNotification(null)}>
          <Alert severity={notification.severity} onClose={() => setNotification(null)} sx={{ width: "100%" }}>
            {notification.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

const NoDataMessage = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
    <Typography variant="body1" color="text.secondary">
      No data available for the selected time range
    </Typography>
  </Box>
);

export default WorkflowsMonitoring;
